import { useState } from "react";
import { Link } from "react-router-dom";
import { Loader2, Lock, LogOut, Search, ShieldCheck, AlertTriangle, Gift } from "lucide-react";
import axios from "axios";
import { API, waLink } from "@/lib/api";
import { LogoImg } from "@/components/landing/Logo";

const TABS = [
  { id: "active", label: "Da usare" },
  { id: "used", label: "Utilizzati" },
  { id: "all", label: "Tutti (anche non pagati)" },
];

export default function Admin() {
  const [pin, setPin] = useState(sessionStorage.getItem("admin_pin") || "");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");
  const [tab, setTab] = useState("active");
  const [query, setQuery] = useState("");
  const [gift, setGift] = useState({ package_id: "single", name: "", email: "", phone: "" });
  const [giftLoading, setGiftLoading] = useState(false);
  const [giftResult, setGiftResult] = useState(null);

  const sendGift = async (e) => {
    e.preventDefault();
    setGiftLoading(true);
    setGiftResult(null);
    setNotice("");
    try {
      const { data: g } = await axios.post(`${API}/admin/gift`, gift, { headers: { "x-admin-pin": pin } });
      setGiftResult(g);
      await load(pin);
    } catch (err) {
      setNotice(err.response?.data?.detail || "Creazione voucher regalo fallita.");
    } finally {
      setGiftLoading(false);
    }
  };

  const load = async (pinValue) => {
    setLoading(true);
    setError("");
    try {
      const { data } = await axios.get(`${API}/admin/overview`, { headers: { "x-admin-pin": pinValue } });
      setData(data);
      setPin(pinValue);
      sessionStorage.setItem("admin_pin", pinValue);
    } catch {
      setError("PIN non valido.");
      sessionStorage.removeItem("admin_pin");
    } finally {
      setLoading(false);
    }
  };

  const toggleRedeem = async (code, redeem) => {
    if (redeem && !window.confirm(`Confermi l'utilizzo del voucher ${code}? L'operazione lo invalida per sempre.`)) return;
    setNotice("");
    try {
      await axios.post(`${API}/admin/redeem`, { credit_code: code, redeem }, { headers: { "x-admin-pin": pin } });
      await load(pin);
    } catch (e) {
      setNotice(e.response?.data?.detail || "Operazione non riuscita.");
    }
  };

  const logout = () => {
    sessionStorage.removeItem("admin_pin");
    setPin("");
    setData(null);
  };

  const fmtDate = (iso) =>
    iso ? new Date(iso).toLocaleDateString("it-IT", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" }) : "—";
  const fmtEur = (cents) => ((cents || 0) / 100).toLocaleString("it-IT", { style: "currency", currency: "EUR" });

  const q = query.trim().toLowerCase();
  const filtered = (data?.orders || []).filter((o) => {
    if (tab === "active" && !(o.payment_status === "paid" && !o.redeemed)) return false;
    if (tab === "used" && !(o.payment_status === "paid" && o.redeemed)) return false;
    if (!q) return true;
    return [o.credit_code, o.customer_name, o.customer_email, o.customer_phone]
      .filter(Boolean)
      .some((f) => f.toLowerCase().includes(q));
  });
  const exactMatch = q && (data?.orders || []).find((o) => o.credit_code?.toLowerCase() === q);

  return (
    <div className="min-h-screen bg-alabaster px-6 py-14" data-testid="admin-page">
      <div className="max-w-4xl mx-auto">
        <div className="flex items-center justify-between mb-10">
          <Link to="/" data-testid="admin-logo">
            <LogoImg className="h-12 w-auto" />
          </Link>
          <div className="flex items-center gap-4">
            <Link to="/" className="text-sm font-medium text-sub hover:text-moss transition-colors">
              ← Home
            </Link>
            {data && (
              <button onClick={logout} data-testid="admin-logout-button" className="inline-flex items-center gap-1.5 text-sm font-medium text-terracotta hover:underline">
                <LogOut size={14} /> Esci
              </button>
            )}
          </div>
        </div>

        {!data ? (
          <div className="max-w-md mx-auto rounded-[2rem] bg-white border border-line p-10 text-center">
            <Lock size={36} className="mx-auto text-moss" />
            <h1 className="mt-4 font-serif text-2xl font-semibold text-moss">Area Riservata</h1>
            <p className="mt-2 text-sm text-sub">Inserisci il tuo PIN per vedere ordini, saldi e voucher.</p>
            <form
              onSubmit={(e) => {
                e.preventDefault();
                load(e.target.elements.pin.value);
              }}
              className="mt-6 space-y-3"
            >
              <input
                name="pin"
                type="password"
                required
                data-testid="admin-pin-input"
                placeholder="PIN amministratore"
                className="w-full rounded-2xl border border-line bg-alabaster px-5 py-3.5 text-center text-lg tracking-widest focus:outline-none focus:ring-2 focus:ring-moss/40"
              />
              <button
                type="submit"
                disabled={loading}
                data-testid="admin-login-button"
                className="w-full rounded-full bg-moss py-3.5 text-base font-semibold text-alabaster transition-all duration-200 hover:bg-mosslight disabled:opacity-60"
              >
                {loading ? <Loader2 size={18} className="animate-spin inline" /> : "Accedi"}
              </button>
              {error && <p className="text-sm text-terracotta font-medium" data-testid="admin-error">{error}</p>}
            </form>
          </div>
        ) : (
          <>
            <h1 className="font-serif text-3xl font-semibold tracking-tight text-moss">Cruscotto ordini</h1>

            <div className="mt-6 grid grid-cols-2 lg:grid-cols-4 gap-4">
              {[
                { label: "Incassato lordo", value: data.stats.gross_eur.toLocaleString("it-IT", { style: "currency", currency: "EUR" }), testid: "stat-gross" },
                { label: "Consulenze attive", value: data.stats.credits_active, testid: "stat-credits-active" },
                { label: "Voucher attivi", value: data.stats.vouchers_active, testid: "stat-vouchers-active" },
                { label: "Voucher utilizzati", value: data.stats.vouchers_used, testid: "stat-vouchers-used" },
              ].map((s) => (
                <div key={s.label} className="rounded-3xl bg-white border border-line p-5" data-testid={s.testid}>
                  <p className="font-serif text-2xl font-bold text-moss">{s.value}</p>
                  <p className="mt-1 text-[11px] uppercase tracking-widest font-mono text-sub">{s.label}</p>
                </div>
              ))}
            </div>

            <div className="mt-8 rounded-3xl bg-moss p-6" data-testid="verify-box">
              <p className="font-mono text-[11px] uppercase tracking-[0.25em] text-gold font-bold mb-3">
                Anti-truffa: verifica il codice prima della chiamata
              </p>
              <div className="relative">
                <Search size={18} className="absolute left-4 top-1/2 -translate-y-1/2 text-alabaster/50" />
                <input
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  data-testid="admin-search-input"
                  placeholder="Incolla qui il codice ricevuto su WhatsApp (es. VIP-1234) o cerca per nome, email, telefono"
                  className="w-full rounded-2xl bg-mosslight/40 border border-mosslight/50 pl-11 pr-4 py-3.5 text-sm text-alabaster placeholder:text-alabaster/40 focus:outline-none focus:ring-2 focus:ring-gold/50"
                />
              </div>
              {exactMatch && (
                <div className="mt-4" data-testid="verify-result">
                  {exactMatch.payment_status !== "paid" ? (
                    <p className="inline-flex items-center gap-2 rounded-full bg-terracotta px-5 py-2.5 text-sm font-bold text-alabaster">
                      <AlertTriangle size={16} /> Codice non pagato, non fare la consulenza
                    </p>
                  ) : exactMatch.redeemed ? (
                    <p className="inline-flex items-center gap-2 rounded-full bg-terracotta px-5 py-2.5 text-sm font-bold text-alabaster">
                      <AlertTriangle size={16} /> Già utilizzato il {fmtDate(exactMatch.redeemed_at)}, non riusarlo
                    </p>
                  ) : !exactMatch.available_now ? (
                    <p className="inline-flex items-center gap-2 rounded-full bg-gold px-5 py-2.5 text-sm font-bold text-mossdeep">
                      <AlertTriangle size={16} /> Valido ma fuori fascia: utilizzabile {exactMatch.schedule_label}
                    </p>
                  ) : (
                    <p className="inline-flex items-center gap-2 rounded-full bg-gold px-5 py-2.5 text-sm font-bold text-mossdeep">
                      <ShieldCheck size={16} /> Valido e utilizzabile adesso, procedi pure
                    </p>
                  )}
                </div>
              )}
            </div>

            {notice && (
              <p className="mt-4 rounded-2xl bg-terracotta/10 border border-terracotta/40 px-5 py-3 text-sm font-semibold text-terracotta" data-testid="admin-notice">
                {notice}
              </p>
            )}

            <div className="mt-8 rounded-3xl bg-white border border-gold/50 p-6" data-testid="gift-box">
              <p className="font-mono text-[11px] uppercase tracking-[0.25em] text-terracotta font-bold mb-1 flex items-center gap-2">
                <Gift size={14} /> Regala un voucher
              </p>
              <p className="text-xs text-sub mb-4">Crea un voucher gratuito per un cliente: codice immediato e email automatica col codice.</p>
              <form onSubmit={sendGift} className="grid gap-3 sm:grid-cols-2" data-testid="gift-form">
                <select
                  value={gift.package_id}
                  onChange={(e) => setGift((g) => ({ ...g, package_id: e.target.value }))}
                  data-testid="gift-tier-select"
                  className="rounded-2xl border border-line bg-alabaster px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-moss/40"
                >
                  <option value="single">Voucher Base</option>
                  <option value="premium">Voucher Premium</option>
                  <option value="business">Voucher VIP</option>
                </select>
                <input
                  required
                  value={gift.name}
                  onChange={(e) => setGift((g) => ({ ...g, name: e.target.value }))}
                  data-testid="gift-name-input"
                  placeholder="Nome e cognome del cliente"
                  className="rounded-2xl border border-line bg-alabaster px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-moss/40"
                />
                <input
                  required
                  type="email"
                  value={gift.email}
                  onChange={(e) => setGift((g) => ({ ...g, email: e.target.value }))}
                  data-testid="gift-email-input"
                  placeholder="Email del cliente"
                  className="rounded-2xl border border-line bg-alabaster px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-moss/40"
                />
                <input
                  value={gift.phone}
                  onChange={(e) => setGift((g) => ({ ...g, phone: e.target.value }))}
                  data-testid="gift-phone-input"
                  placeholder="Telefono (facoltativo)"
                  className="rounded-2xl border border-line bg-alabaster px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-moss/40"
                />
                <button
                  type="submit"
                  disabled={giftLoading}
                  data-testid="gift-submit-button"
                  className="sm:col-span-2 inline-flex items-center justify-center gap-2 rounded-full bg-terracotta py-3.5 text-sm font-bold uppercase tracking-wider text-alabaster transition-all duration-200 hover:bg-terrahover disabled:opacity-60"
                >
                  {giftLoading ? <Loader2 size={16} className="animate-spin" /> : <Gift size={16} />}
                  Crea e invia voucher regalo
                </button>
              </form>
              {giftResult && (
                <div className="mt-4 rounded-2xl bg-sage/60 border border-mosslight/30 p-5 text-center" data-testid="gift-result">
                  <p className="text-xs uppercase tracking-widest font-mono text-sub">{giftResult.tier} creato</p>
                  <p className="font-mono text-2xl font-bold text-moss mt-1">{giftResult.credit_code}</p>
                  <p className="text-xs text-sub mt-1">
                    {giftResult.email_sent ? "Email col codice inviata al cliente." : "Email NON inviata, gira il codice a mano."}
                  </p>
                  <a
                    href={waLink(`${(giftResult.tier || "Voucher").toUpperCase()} — Codice ${giftResult.credit_code} (regalo). Buongiorno, le giro il voucher omaggio per la consulenza.`)}
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid="gift-whatsapp-link"
                    className="mt-3 inline-block text-xs font-semibold text-terracotta hover:underline"
                  >
                    Oppure invialo via WhatsApp →
                  </a>
                </div>
              )}
            </div>

            <div className="mt-8 flex flex-wrap gap-2" data-testid="admin-tabs">
              {TABS.map((t) => (
                <button
                  key={t.id}
                  onClick={() => setTab(t.id)}
                  data-testid={`tab-${t.id}`}
                  className={`rounded-full px-5 py-2.5 text-sm font-semibold transition-all duration-200 ${
                    tab === t.id ? "bg-moss text-alabaster" : "bg-white border border-line text-sub hover:border-moss/40"
                  }`}
                >
                  {t.label}
                </button>
              ))}
            </div>

            <div className="mt-5 space-y-4" data-testid="admin-orders-table">
              {filtered.length === 0 && (
                <p className="text-sub text-sm" data-testid="orders-empty">Nessun voucher in questa vista.</p>
              )}
              {filtered.map((o) => (
                <div
                  key={o.credit_code + o.created_at}
                  data-testid={`order-${o.credit_code}`}
                  className={`rounded-3xl border p-6 ${o.payment_status === "paid" ? (o.redeemed ? "bg-tint border-line opacity-75" : "bg-white border-moss/20") : "bg-white border-line opacity-60"}`}
                >
                  <div className="flex flex-wrap items-start justify-between gap-4">
                    <div>
                      <div className="flex flex-wrap items-center gap-3">
                        <p className="font-mono font-semibold text-lg tracking-wide text-ink">{o.credit_code}</p>
                        <span className={`rounded-full px-3 py-1 text-[11px] font-bold uppercase tracking-wider ${
                          o.payment_status !== "paid" ? "bg-line text-sub"
                          : o.redeemed ? "bg-line text-sub"
                          : "bg-sage text-sagetext"}`}
                          data-testid={`order-status-${o.credit_code}`}>
                          {o.payment_status !== "paid" ? o.payment_status : o.redeemed ? "Utilizzato" : "Attivo"}
                        </span>
                        {o.payment_status === "paid" && !o.redeemed && (
                          o.available_now ? (
                            <span className="rounded-full bg-moss px-3 py-1 text-[11px] font-bold uppercase tracking-wider text-gold" data-testid={`order-window-${o.credit_code}`}>
                              Utilizzabile ora
                            </span>
                          ) : (
                            <span className="rounded-full bg-gold/20 px-3 py-1 text-[11px] font-bold uppercase tracking-wider text-[#8a6a24]" data-testid={`order-window-${o.credit_code}`}>
                              Fuori fascia ora
                            </span>
                          )
                        )}
                      </div>
                      <p className="mt-1.5 text-sm font-semibold text-ink">
                        {o.customer_name} · {fmtEur(o.amount)} · {o.package_name}
                      </p>
                      <p className="text-xs text-sub mt-0.5">
                        {o.customer_email} · {o.customer_phone} · {fmtDate(o.created_at)}
                      </p>
                      {o.schedule_label && (
                        <p className="text-xs font-semibold text-mosslight mt-0.5">
                          Utilizzabile {o.schedule_label}
                        </p>
                      )}
                      {o.problem && <p className="text-xs text-sub mt-1 italic">"{o.problem}"</p>}
                      {o.redeemed_at && <p className="text-xs text-sub mt-1">Utilizzato il {fmtDate(o.redeemed_at)}</p>}
                    </div>
                    {o.payment_status === "paid" && (
                      <button
                        onClick={() => toggleRedeem(o.credit_code, !o.redeemed)}
                        data-testid={`redeem-${o.credit_code}`}
                        className={o.redeemed
                          ? "rounded-full border-2 border-moss/30 px-5 py-2.5 text-xs font-bold uppercase tracking-wider text-moss hover:bg-sage transition-colors"
                          : "rounded-full bg-terracotta px-5 py-2.5 text-xs font-bold uppercase tracking-wider text-alabaster hover:bg-terrahover transition-colors"}
                      >
                        {o.redeemed ? "Riattiva" : "Segna utilizzato"}
                      </button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </>
        )}
      </div>
    </div>
  );
}
